import { Table, Row, Col } from "react-bootstrap";
import { useCallback, useContext, useEffect, useState } from "react";
import { WEEKDAYS, SYSTOLIC, DIASTOLIC, SYS, DIA, PULSE, OVERNIGHT, FASTING, BREAKFAST, POST_BREAKFAST, LUNCH, POST_LUNCH, DINNER, POST_DINNER } from "../../util/const";
import { GetHourList, GetTimeInterval, Initialise} from "../../util/data-utils";
import { ReviewContext } from "../ReviewPatient";
import React from "react";
import { Bp } from "../../types/observation";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import DataNavigator from "../../Components/DataNavigator";
import { Patient } from "../../types/patient";


const BloodPressure = () => {
  
  const context = useContext(ReviewContext);
  const patient = context.patient;

  const systolicLower = parseFloat(patient.sysLower);
  const systolicUpper = parseFloat(patient.sysUpper);
  const diastolicLower = parseFloat(patient.diaLower);
  const diastolicUpper = parseFloat(patient.diaUpper);

  var overnightInterval = GetTimeInterval(patient.overnightEnd, patient.overnightStart)
  var fastingInterval = GetTimeInterval( patient.fastingEnd, patient.fastingStart)
  var breakfastInterval =  GetTimeInterval(patient.breakfastStart, patient.fastingEnd)
  var postbreakfastInterval = GetTimeInterval(patient.breakfastEnd, patient.breakfastStart)
  var lunchInterval = GetTimeInterval(patient.lunchStart, patient.breakfastEnd)
  var postlunchInterval = GetTimeInterval(patient.lunchEnd, patient.lunchStart)
  var dinnerInterval = GetTimeInterval(patient.dinnerStart, patient.lunchEnd)
  var postdinnerInterval = GetTimeInterval(patient.dinnerEnd, patient.dinnerStart)

  var today = new Date(context.weekEnding);
  var timeInMillis = today.getTime();
  var day = today.getDay() + 1; // day as weekday number
  var dayCounter = 0;

  const setTimeslotForElement = (element: Bp) => {
    var rtStr = element.recordedTime.split(":");
    var recordedDate = new Date(element.recordedDate);
    recordedDate.setHours(+rtStr[0]);
    recordedDate.setMinutes(+rtStr[1]);
    recordedDate.setSeconds(+rtStr[2]);
    element.weekDay = recordedDate.getDay();
  };

  const getHourKey = (time: string) => {
    var hour = time.split(":");
    return hour[0];
  };

  const [bpHourlyMap, setBglHourlyMap] = useState<Map<string, Map<string, Array<Bp>>>>();

  const loadData = useCallback((aPatient: Patient) => {
    const dataMap = () => {
      var bpMap = new Map<string, Map<string, Array<Bp>>>();
      if (aPatient.observations !== undefined && aPatient.observations.size > 0 && aPatient.observations.has("bp")) 
      {
        var bps: Array<Bp> = aPatient.observations.get("bp") as Array<Bp>;
        if (bps !== undefined) 
        {
          Initialise(aPatient);
          bps.forEach((element) => {
            setTimeslotForElement(element);
            var hourKey = getHourKey(element.recordedTime.toString());
            var weekDayKey = WEEKDAYS[element.weekDay];
            if (bpMap.has(weekDayKey)) {
              var hourValues = bpMap.get(weekDayKey);
              if (hourValues?.has(hourKey)) {
                var values = hourValues.get(hourKey);
                values?.push(element);
                hourValues.set(hourKey, values!);
                bpMap.set(weekDayKey, hourValues);
              } else {
                var elements = [element];
                hourValues?.set(hourKey, elements);
                bpMap.set(weekDayKey, hourValues!);
              }
            } else {
              var newHourValues = new Map<string, Array<Bp>>([[hourKey, [element]]]);
              bpMap.set(weekDayKey, newHourValues);
            }
          });  
        }
      }
      return bpMap;
    }
    let bpMap = dataMap();
    setBglHourlyMap(bpMap);
  },[]);

  useEffect(() => {
    loadData(context.patient);
  },[context, loadData]);

  const displayData = (bpMap: Map<string, Array<Bp>> | undefined) => {
    let display = [];
    let hoursList = GetHourList(patient);

    if (bpMap === undefined) {
      for (let i = 0; i < 24; i++) {
        let displayElement = <td key={i}></td>;
        display.push(displayElement);
      }
    } else {
      let keys = Array.from(bpMap.keys());
      let keysList = convertStringToNumber(keys);

      for (let i = 0; i < 24; i++) {
        var key = hoursList[i];
        if (keysList.indexOf(key) > -1) {
          let index = keysList.indexOf(key);

          var elements = bpMap.get(keys[index])!;
          let result = [];
          let result_detail = [];
          for (var element of elements) {
            if (result_detail.length >= 3) {
              result_detail.push(<hr></hr>);
            }
            if (element.systolic !== undefined) {
              let systolicNumber: number = parseFloat(element.systolic);
              if (systolicNumber < systolicLower && systolicLower !== undefined) {
                result.push(
                  <span>
                    <div className="low-bp">
                      {SYS}: {element.systolic}
                    </div>
                  </span>
                );
                result_detail.push(
                  <div className="low-bp">
                    <span>
                      <div>
                        {SYS}: {element.systolic}
                      </div>
                    </span>
                    {/* <div>Time: {element.recordedTime}</div> */}
                  </div>
                );
              } else if (systolicNumber > systolicUpper && systolicUpper !== undefined) {
                result.push(
                  <span>
                    <div className="high-bp">
                      {SYS}: {element.systolic}
                    </div>
                  </span>
                );
                result_detail.push(
                  <div className="high-bp">
                    <span>
                      <div>
                        {SYS}: {element.systolic}
                      </div>
                    </span>
                    {/* <div>Time: {element.recordedTime}</div> */}
                  </div>
                );
              } else {
                result.push(
                  <span>
                    <div className="bp">
                      {SYS}: {element.systolic}
                    </div>
                  </span>
                );
                result_detail.push(
                  <div className="bp">
                    <span>
                      <div>
                        {SYS}: {element.systolic}
                      </div>
                    </span>
                    {/* <div>Time: {element.recordedTime}</div> */}
                  </div>
                );
              }
            }

            if (element.diastolic !== undefined) {
              let diastolicNumber: number = parseFloat(element.diastolic);
              if (diastolicNumber < diastolicLower && diastolicLower !== undefined) {
                result.push(
                  <div className="low-bp">
                    {DIA}: {element.diastolic}
                  </div>
                );
                result_detail.push(
                  <div className="low-bp">
                    <div>
                      {DIA}: {element.diastolic}
                    </div>
                    {/* <div>Time: {element.recordedTime}</div> */}
                  </div>
                );
              } else if (diastolicNumber > diastolicUpper && diastolicUpper !== undefined) {
                result.push(
                  <div className="high-bp">
                    {DIA}: {element.diastolic}
                  </div>
                );
                result_detail.push(
                  <div className="high-bp">
                    <div>
                      {DIA}: {element.diastolic}
                    </div>
                    {/* <div>Time: {element.recordedTime}</div> */}
                  </div>
                );
              } else {
                result.push(
                  <div className="bp">
                    {DIA}: {element.diastolic}
                  </div>
                );
                result_detail.push(
                  <div className="bp">
                    <div>
                      {DIA}: {element.diastolic}
                    </div>
                    {/* <div>Time: {element.recordedTime}</div> */}
                  </div>
                );
              }
            }

            if (element.heartRate !== undefined) {
              result.push(
                <div className="bp">
                  {PULSE}: {element.heartRate}
                </div>
              );

              result_detail.push(
                <div className="bp">
                  <div>
                    {PULSE}: {element.heartRate}
                  </div>
                  <div>Time: {element.recordedTime}</div>
                </div>
              );
            }
          }
          if (result.length > 3) {
            display.push(
              <td key={i}>
                <OverlayTrigger
                  placement="right"
                  delay={{ show: 250, hide: 400 }}
                  overlay={<Tooltip className="tooltip">{result_detail}</Tooltip>}>
                  <span>{result.slice(0, 3)}...</span>
                </OverlayTrigger>
              </td>
            );
          } else {
            display.push(
              <td key={i}>
                <OverlayTrigger
                  placement="right"
                  delay={{ show: 250, hide: 400 }}
                  overlay={<Tooltip className="tooltip">{result_detail}</Tooltip>}>
                  <span>{result}</span>
                </OverlayTrigger>
              </td>
            );
          }
        } else {
          let displayElement = <td key={i}></td>;
          display.push(displayElement);
        }
      }
    }
    return display;
  };

  const convertStringToNumber = (arrayList: Array<string>) => {
    let newList = [];
    for (let i = 0; i < arrayList.length; i++) {
      newList.push(Number(arrayList[i]));
    }
    return newList;
  };

  const getTds = (patient:Patient) => {
    var hoursList = GetHourList(patient);

    let tdList = [];
    for (let i = 0; i < hoursList.length; i++) {
      const hours = hoursList[i].toString().padStart(2, "0");
      const minutes = "00";
      tdList.push(
        <td>
          {hours}:{minutes}
        </td>
      );
    }
    return tdList;
  };

  return (
    <Table striped bordered hover className="review-table">
      <thead>
        <tr>
          <th colSpan={25}>
            <Row>
              <Col className="left">
                <DataNavigator typeKey="bp" />
              </Col>
              {isNaN(diastolicLower) && isNaN(diastolicUpper) ? (
                <Col className="right me-2">
                  {SYSTOLIC}: {patient.sysUpper}/{patient.sysLower}
                </Col>
              ) : (
                <Col className="right me-2">
                  {SYSTOLIC}: {patient.sysUpper}/{patient.sysLower} ---
                  {DIASTOLIC}: {patient.diaUpper}/{patient.diaLower}
                </Col>
              )}
            </Row>
          </th>
        </tr>
        <tr><th colSpan={25} className="section"></th></tr>
        <tr>
          <th></th>
          <th colSpan={overnightInterval}>{OVERNIGHT}</th>
          <th colSpan={fastingInterval}>{FASTING}</th>
          <th colSpan={breakfastInterval}>{BREAKFAST}</th>
          <th colSpan={postbreakfastInterval}>{POST_BREAKFAST}</th>
          <th colSpan={lunchInterval}>{LUNCH}</th>
          <th colSpan={postlunchInterval}>{POST_LUNCH}</th>
          <th colSpan={dinnerInterval}>{DINNER}</th>
          <th colSpan={postdinnerInterval}>{POST_DINNER}</th>
        </tr>
        <tr>
          <th></th>
          {getTds(patient)}
        </tr>
      </thead>
      <tbody>
        { WEEKDAYS.map(() => {
          day = day - 1;
          if (day < 0) {
            day = 7 + day;
          }
          var dayKey = WEEKDAYS[day];

          var nextDate = new Date(timeInMillis - dayCounter * 24 * 3600 * 1000);
          var dayOfMonthStr = nextDate.getDate().toString();
          if(dayOfMonthStr.length === 1)
            dayOfMonthStr = "0" + dayOfMonthStr;
          dayCounter++;

          var bpMap = bpHourlyMap?.get(dayKey);
          return (
            <React.Fragment key={dayKey}>
              <tr>
                <td>{dayKey} {dayOfMonthStr}</td>
                {displayData(bpMap)}
              </tr>
            </React.Fragment>
          );
        })}
      </tbody>
    </Table>
  );
};

export default BloodPressure;
